import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav, { CREATOR_SIDEBAR_WIDTH, CREATOR_BREAKPOINT } from '../components/AdminNav';
import { theme, font, pillButton, outlineButton, inputStyle, labelStyle } from '../components/adminTheme';
import stripExif from '../utils/stripExif';
import { uploadToB2 } from '../utils/uploadToB2';

function NewPost() {
    const navigate = useNavigate();
    const [orgUsers, setOrgUsers] = useState([]);
    const [coAuthors, setCoAuthors] = useState([]);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [author, setAuthor] = useState('');
    const [coverFile, setCoverFile] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [isStatement, setIsStatement] = useState(false);
    const [postLocation, setPostLocation] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [confirmStep, setConfirmStep] = useState(0); // 0 = fermé, 1 = première confirmation, 2 = seconde

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    useEffect(() => {
        const checkAndFetchUser = async () => {
            const { data } = await supabase.auth.getSession();

            if (!data.session) {
                navigate('/login');
                return;
            }

            const { data: userData } = await supabase
                .from('users')
                .select('username, first_name, last_name, role')
                .eq('email', data.session.user.email)
                .single();

            if (userData) {
                setUsername(userData.username);
                setRole(userData.role);
                setAuthor(`${userData.first_name} ${userData.last_name}`);
                if (userData.role === 'ORGANISATION' || userData.role === 'ADMIN') {
                    const { data: orgs } = await supabase
                        .from('users')
                        .select('username, first_name, last_name')
                        .eq('role', 'ORGANISATION')
                        .neq('username', userData.username);
                    if (orgs) setOrgUsers(orgs);
                }
            }
        };
        checkAndFetchUser();
    }, [navigate]);

    const handlePublish = async () => {
        if (!title || !content) { setConfirmStep(0); return; }
        setPublishing(true);
        setConfirmStep(0);

        const now = new Date();
        const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
        const slug = `${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}-${username}-${dateStr}`;

        let coverImageUrl = '';
        try {
            if (coverFile) {
                const cleanFile = await stripExif(coverFile);
                coverImageUrl = await uploadToB2(`covers/blog/${slug}/${cleanFile.name}`, cleanFile);
            }

            const { error: insertError } = await supabase.from('posts').insert({
                title,
                slug,
                content,
                author,
                author_username: username,
                co_authors: coAuthors,
                cover_image: coverImageUrl,
                created_at: new Date().toISOString(),
                is_statement: isStatement,
                location: isStatement ? postLocation : '',
                link_url: linkUrl,
            });

            if (insertError) {
                console.error("Erreur insertion article:", insertError);
                alert("Erreur lors de la publication.");
            } else {
                navigate('/blog');
            }
        } catch (err) {
            console.error("Erreur upload cover:", err);
            alert("Erreur lors de la publication.");
        }

        setPublishing(false);
    };

    return (
        <div style={{ minHeight: '100vh', background: theme.bg }}>
            <style>{`
                .cpost-form-content {
                    margin-left: ${CREATOR_SIDEBAR_WIDTH}px;
                    max-width: 680px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${CREATOR_BREAKPOINT}px) {
                    .cpost-form-content { margin-left: 0; padding: 88px 24px 60px; }
                }
                .cpost-dropzone {
                    border: 1.5px dashed ${theme.border};
                    padding: 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: border-color .2s ease;
                }
                .cpost-dropzone:hover { border-color: ${theme.borderHover}; }
            `}</style>

            <AdminNav username={username} role={role} onLogout={handleLogout} activePath="/admin/new-post" />

            <div className="cpost-form-content">
                <h1 style={{ fontFamily: font, fontWeight: 700, fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.01em', color: theme.text, margin: '0 0 28px' }}>
                    New post
                </h1>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Title</p>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Post title" style={{ ...inputStyle, fontSize: '16px' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Author</p>
                    <p style={{ fontFamily: font, fontSize: '14px', fontWeight: 500, color: theme.text, padding: '10px 0', margin: 0 }}>
                        {author} <span style={{ color: theme.textMuted, fontSize: '12px' }}>@{username}</span>
                    </p>
                </div>

                {(role === 'ORGANISATION' || role === 'ADMIN') && orgUsers.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <p style={labelStyle}>Co-authors (organisations)</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                            {orgUsers.map(u => {
                                const selected = coAuthors.includes(u.username);
                                const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                                return (
                                    <button
                                        key={u.username}
                                        type="button"
                                        onClick={() => setCoAuthors(prev =>
                                            selected ? prev.filter(c => c !== u.username) : [...prev, u.username]
                                        )}
                                        className="wlink"
                                        style={{
                                            background: 'none', border: 'none', fontSize: '12px', cursor: 'pointer', fontFamily: font,
                                            fontWeight: selected ? 700 : 500,
                                            color: selected ? theme.text : theme.textMuted,
                                        }}
                                    >
                                        {label} <span style={{ opacity: 0.6 }}>@{u.username}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <p style={{ fontFamily: font, fontSize: '10px', fontWeight: 500, color: theme.textMuted, margin: '8px 0 0' }}>
                            Le post sera publié comme une collaboration officielle entre les organisations sélectionnées.
                        </p>
                    </div>
                )}
                {(role === 'ORGANISATION' || role === 'ADMIN') && (
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={isStatement} onChange={e => setIsStatement(e.target.checked)} />
                            <span style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.text }}>Mark as Official Statement</span>
                        </label>
                        <p style={{ fontFamily: font, fontSize: '10px', fontWeight: 500, color: theme.textMuted, margin: '4px 0 0', paddingLeft: '24px' }}>This post will be badged as an official statement from winston.ie.</p>
                    </div>
                )}

                {isStatement && (
                    <div style={{ marginBottom: '16px' }}>
                        <p style={labelStyle}>Location</p>
                        <input type="text" value={postLocation} onChange={e => setPostLocation(e.target.value)} placeholder="Montréal, Québec, Canada" style={inputStyle} />
                    </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Link (optionnel)</p>
                    <input type="text" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://... ou /region/canada/mon-album" style={inputStyle} />
                    <p style={{ fontFamily: font, fontSize: '10.5px', fontWeight: 500, color: theme.textMuted, margin: '6px 0 0' }}>Lien vers une page winston.ie ou un site externe.</p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <p style={labelStyle}>Cover image</p>
                    <div className="cpost-dropzone" onClick={() => document.getElementById('coverInput').click()}>
                        <p style={{ fontFamily: font, color: theme.textMuted, fontSize: '14px', fontWeight: 500, margin: 0 }}>{coverFile ? coverFile.name : 'Click to select cover image'}</p>
                        <input id="coverInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setCoverFile(e.target.files[0])} />
                    </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <p style={labelStyle}>Content</p>
                    <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your post here..." rows={16} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.7' }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', alignItems: 'center' }}>
                    <button className="wlink" style={outlineButton} onClick={() => navigate('/admin/dashboard')}>Cancel</button>
                    <button
                        className="wlink"
                        style={pillButton}
                        onClick={() => {
                            if (!title || !content) return;
                            setConfirmStep(1);
                        }}
                        disabled={publishing}
                    >
                        {publishing ? 'Publishing...' : 'Publish post'}
                    </button>
                </div>
            </div>

            {confirmStep > 0 && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
                    <div style={{ background: theme.card, padding: '28px', width: 'min(360px, calc(100vw - 48px))', boxSizing: 'border-box' }}>
                        {confirmStep === 1 && (
                            <>
                                <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>Publier "{title}" ?</h3>
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, marginBottom: '20px' }}>
                                    Ce post sera visible publiquement sur blog.ie.
                                </p>
                                <div style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => setConfirmStep(0)}>Annuler</button>
                                    <button className="wlink" style={pillButton} onClick={() => setConfirmStep(2)}>Continuer</button>
                                </div>
                            </>
                        )}
                        {confirmStep === 2 && (
                            <>
                                <h3 style={{ fontFamily: font, fontWeight: 700, marginBottom: '8px', fontSize: '15px', color: theme.text }}>Confirme une dernière fois</h3>
                                <p style={{ fontFamily: font, fontSize: '13px', fontWeight: 500, color: theme.textMuted, marginBottom: '20px' }}>
                                    Es-tu sûr de vouloir publier maintenant ?
                                </p>
                                <div style={{ display: 'flex', gap: '20px', justifyContent: 'flex-end' }}>
                                    <button className="wlink" style={outlineButton} onClick={() => setConfirmStep(0)}>Annuler</button>
                                    <button className="wlink" style={pillButton} onClick={handlePublish}>Publier</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewPost;